import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RiFileTextLine, RiMailLine, RiTimeLine, RiArrowRightLine, RiDeleteBinLine, RiArticleLine } from 'react-icons/ri';
import GlassCard from '../ui/v2/GlassCard';
import GlassButton from '../ui/v2/GlassButton';

interface HistoryItem {
    id: string;
    title: string;
    subtitle?: string;
    date: string;
    type: 'resume' | 'cover_letter';
}

interface DocumentHistoryProps {
    items: HistoryItem[];
    type: 'resume' | 'cover_letter';
    isLoading?: boolean;
    onDelete?: (id: string) => void;
}

export default function DocumentHistory({ items, type, isLoading, onDelete }: DocumentHistoryProps) {
    const navigate = useNavigate();

    if (isLoading) {
        return (
            <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />
                ))}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    {type === 'resume' ? (
                        <RiFileTextLine className="text-2xl text-gray-500" />
                    ) : (
                        <RiArticleLine className="text-2xl text-gray-500" />
                    )}
                </div>
                <h3 className="text-lg font-medium text-white mb-2">No history yet</h3>
                <p className="text-gray-400 max-w-xs">
                    Generate your first {type === 'resume' ? 'resume' : 'cover letter'} to see it here.
                </p>
            </div>
        );
    }

    const handleItemClick = (id: string) => {
        if (type === 'resume') {
            navigate(`/app/resumes/${id}/editor`);
        } else {
            navigate(`/app/cover-letters/${id}/editor`);
        }
    };

    return (
        <div className="space-y-3">
            {items.map((item) => (
                <GlassCard
                    key={item.id}
                    className="!p-4 flex items-center justify-between group cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => handleItemClick(item.id)}
                >
                    <div className="flex items-center gap-4">
                        <div className={`
                            w-10 h-10 rounded-lg flex items-center justify-center
                            ${type === 'resume' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}
                        `}>
                            {type === 'resume' ? <RiFileTextLine /> : <RiArticleLine />}
                        </div>
                        <div>
                            <h4 className="font-medium text-white group-hover:text-brand-400 transition-colors">
                                {item.title || 'Untitled Document'}
                            </h4>
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                                {item.subtitle && (
                                    <span>{item.subtitle}</span>
                                )}
                                <span className="flex items-center gap-1">
                                    <RiTimeLine className="text-[10px]" />
                                    {new Date(item.date).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {onDelete && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(item.id);
                                }}
                                className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                            >
                                <RiDeleteBinLine />
                            </button>
                        )}
                        <RiArrowRightLine className="text-gray-400 group-hover:text-white transition-colors" />
                    </div>
                </GlassCard>
            ))}
        </div>
    );
}
